import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { LineReveal, ScrollReveal, Magnetic } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Wedding", "Music Video", "Film / Web", "Real Estate", "Event", "Other"];
const BUDGETS = ["Under ₹25k", "₹25k – ₹50k", "₹50k – ₹1L", "₹1L+"];

const inputCls =
  "w-full bg-white border border-ink/15 focus:border-goldtext outline-none px-4 py-3.5 text-sm text-ink placeholder:text-neutral-400 transition-colors rounded-lg";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 block mb-2";

const PillGroup = ({ options, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        data-testid={`${testPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(o)}
        className={`border px-4 py-2.5 font-mono text-[11px] tracking-wider uppercase transition-colors rounded-full ${
          value === o ? "border-goldtext text-goldtext bg-goldtext/10" : "border-ink/15 text-neutral-500 hover:border-ink/40"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

export default function ShootPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", project_type: "", date: "", location: "", budget: "", brief: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setWhatsapp(r.data.whatsapp_number || "")).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.name.trim().length > 1 &&
    form.phone.replace(/\D/g, "").length >= 10 &&
    form.project_type &&
    form.brief.trim().length >= 10;

  const waLink = () => {
    const msg = `SHOOT ENQUIRY — filmmakerakki\n\nNaam: ${form.name}\nWhatsApp: ${form.phone}\nEmail: ${form.email || "-"}\nProject: ${form.project_type}\nDate: ${form.date || "-"}\nLocation: ${form.location || "-"}\nBudget: ${form.budget || "-"}\n\nBrief:\n${form.brief}`;
    return `https://wa.me/${whatsapp}?text=${encodeURIComponent(msg)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Naam, WhatsApp number, project type aur brief (10+ letters) zaroori hai.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/shoot-enquiry`, form);
      setDone(true);
      toast.success("Enquiry bhej di gayi!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bhejne mein dikkat aayi. Dobara try karein.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F4F2ED] text-ink" data-testid="shoot-page">
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-16">
        <a href="/" data-testid="shoot-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-neutral-500 hover:text-goldtext transition-colors">
          <ArrowLeft size={14} /> Back to site
        </a>

        <div className="mt-10">
          <LineReveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-goldtext flex items-center gap-3">
              <span className="w-8 h-px bg-goldtext inline-block" /> Hire Me For A Shoot
            </p>
          </LineReveal>
          <h1 className="font-serif uppercase leading-[1.02] text-5xl md:text-7xl mt-6 text-ink">
            LET'S <span className="italic normal-case text-gold-metallic">shoot.</span>
          </h1>
          <p className="mt-6 text-neutral-600 text-base md:text-lg leading-relaxed max-w-xl">
            Wedding, music video, film, real estate ya koi bhi project — brief batao.
            Details seedha Akki ke email aur WhatsApp par pahunchengi.
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 bg-white border border-goldtext/25 rounded-2xl p-10 md:p-14 text-center shadow-[0_30px_80px_-20px_rgba(140,109,31,0.18)]"
            data-testid="shoot-success"
          >
            <CheckCircle2 size={48} className="text-goldtext mx-auto" />
            <h2 className="font-serif text-3xl mt-6 text-ink">
              Enquiry <span className="italic text-gold-metallic">sent.</span>
            </h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              {form.name}, aapka brief Akki ke paas pahunch gaya hai. Wo jald hi aapse contact karega.
            </p>
            {whatsapp && (
              <a
                data-testid="shoot-whatsapp-send"
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 bg-[#25D366] text-white font-mono text-xs font-bold tracking-widest uppercase px-8 py-4 rounded-full hover:opacity-90 transition-opacity"
              >
                <MessageCircle size={16} /> WhatsApp par bhi bhejo
              </a>
            )}
            <div>
              <a href="/" className="mt-6 inline-block font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-goldtext transition-colors">
                ← Back to site
              </a>
            </div>
          </motion.div>
        ) : (
          <ScrollReveal delay={0.1}>
            <form onSubmit={submit} className="mt-12 bg-white border border-ink/10 rounded-2xl p-8 md:p-12 space-y-7 shadow-[0_30px_80px_-30px_rgba(140,109,31,0.2)]" data-testid="shoot-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Naam *</label>
                  <input data-testid="shoot-name-input" value={form.name} onChange={set("name")} placeholder="Aapka naam" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>WhatsApp Number *</label>
                  <input data-testid="shoot-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="98765 43210" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email (optional)</label>
                <input data-testid="shoot-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Project Type *</label>
                <PillGroup options={PROJECT_TYPES} value={form.project_type} onChange={(v) => setForm((f) => ({ ...f, project_type: v }))} testPrefix="shoot-type" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Preferred Shoot Date</label>
                  <input data-testid="shoot-date-input" type="date" value={form.date} onChange={set("date")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Location / City</label>
                  <input data-testid="shoot-location-input" value={form.location} onChange={set("location")} placeholder="Mumbai, Pune..." className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Budget Range</label>
                <PillGroup options={BUDGETS} value={form.budget} onChange={(v) => setForm((f) => ({ ...f, budget: v }))} testPrefix="shoot-budget" />
              </div>
              <div>
                <label className={labelCls}>Project Brief *</label>
                <textarea
                  data-testid="shoot-brief-input"
                  value={form.brief}
                  onChange={set("brief")}
                  rows={4}
                  placeholder="Kya shoot karna hai, kitne din, koi reference..."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <Magnetic>
                <button
                  data-testid="shoot-submit-button"
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-3 bg-[#070606] text-white font-mono text-sm font-bold tracking-widest uppercase py-5 rounded-full hover:bg-goldtext transition-colors disabled:opacity-50"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {sending ? "SENDING..." : "Send Enquiry"}
                </button>
              </Magnetic>
              <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase text-center">
                Seedha Akki ke email + WhatsApp par pahunchega
              </p>
            </form>
          </ScrollReveal>
        )}
      </div>
    </main>
  );
}
